/**
 * Phase 1b — B1 (in-drawer size upgrade) + B2 (gap-closing suggestions).
 *
 * Piece/kg counts are parsed live from each variant's own title text
 * (e.g. "12 sztuk", "1.3 kg (32 sztuki)", "1 KG (24 Pieces)") -- never
 * hardcoded, so the real "1.8 KG" naming (not the task brief's "2 kg")
 * always comes straight from the product data. Re-renders naturally on
 * every cart-drawer refresh because the theme's own CartDrawer class
 * replaces this element's parent innerHTML wholesale on every change (see
 * assets/app.js CartDrawer); this file only needs to (re)compute on
 * connectedCallback and, after its own cart calls, dispatch the theme's
 * existing 'cart:refresh' event to trigger that same official re-render path.
 *
 * B1 shows two distinct offers, never merged: a PRIMARY upgrade (the next
 * larger tier up with a lower per-piece price) and, only when a different,
 * further tier crosses the free-shipping threshold, a SECONDARY "dostawa
 * gratis" option. Swaps always ADD the new variant first and only zero out
 * the original line once that add has succeeded -- never remove-then-add --
 * so a failed add leaves the original line intact and shows a short PL error
 * instead of losing the customer's item.
 *
 * Macaron AOV flow (2026-10-05 task), added alongside B1/B2 rather than as a
 * separate observer: DrawerMacaronOffer counts macaron-box quantity straight
 * from /cart.js (by product id, summed across lines -- two boxes can be one
 * merged line at quantity 2, or two separate lines when their properties
 * differ, e.g. once a _pb_src tag is present on one of them; see DECISIONS.md
 * 2026-10-05 for the empirical cart-merge test this relies on). It never
 * matches the macaron product itself as a B1 upgrade candidate, because that
 * product currently has only one variant -- computeUpgradeCandidate's own
 * "skip my own variant, need >=1 other variant" logic already excludes it,
 * nothing extra was needed there. DrawerGapSuggestions gets suppressed while
 * exactly one macaron box is in the cart (the macaron block replaces it) and,
 * once 2+ boxes are in the cart, gets told to pin a specific product handle
 * (Zestaw Premium) first in its sort -- both via an options object so the
 * existing gap behaviour for every other cart (no macaron, or 0/1 boxes) is
 * untouched.
 *
 * Shared-fetch optimization (2026-10-05): every render() used to fetch
 * /cart.js itself -- three separate, redundant fetches per drawer open/
 * refresh (DrawerUpgrade, DrawerMacaronOffer, DrawerGapSuggestions each
 * called it independently). init() now fetches it exactly once and passes
 * the resolved cart object into all three. DrawerGapSuggestions' gap
 * collection is similarly prefetched once at page load (fetchGapCollection,
 * cached by Promise) and reused on every render -- no new network request
 * for it on the add path once that prefetch has resolved.
 *
 * NOTE (box sizes task, 2026-10-05): a further mobile layout-shift fix (a
 * shared wrapping slot with a reserved min-height, so the footer below these
 * three blocks doesn't jump when they fill in) was built the same day but
 * had not been re-measured on a real device as of this push -- left OUT of
 * this live push on the owner's explicit instruction, pending that
 * confirmation on staging. Only the network-efficiency piece above (which
 * has no device-dependent visual behavior to verify) shipped here.
 */
(function () {
  function parsePieceCount(variantTitle) {
    if (!variantTitle) return null;
    // prefer a parenthetical piece count: "1.3 kg (32 sztuki)" / "1 KG (24 Pieces)"
    var paren = variantTitle.match(/\(\s*(\d+)\s*(?:sztuk|sztuki|pieces)\s*\)/i);
    if (paren) return parseInt(paren[1], 10);
    // else a leading count: "12 sztuk" / "24 sztuki" / "9 Pieces" / "8 Pieces in Premium Box".
    // "sztuki" (2026-10-06 box-sizes fix): Polish plural agreement for 22-24-style counts
    // renders as "sztuki" not "sztuk" (e.g. the macaron box's own "24 sztuki", via a
    // pre-existing per-theme PL translation on that option value) -- the parenthetical
    // pattern above already accounted for this ("32 sztuki"), this one hadn't yet, which
    // silently failed to parse the live "24 sztuki" variant and skipped its +1-box
    // suppression. Backtracking into the longer alternative still requires the \b after
    // it, so "sztuk"-only titles are unaffected.
    var leading = variantTitle.match(/^(\d+)\s*(?:sztuk|sztuki|pieces)\b/i);
    if (leading) return parseInt(leading[1], 10);
    return null;
  }

  function fetchProductJSON(handle, cache) {
    if (cache[handle]) return cache[handle];
    cache[handle] = fetch('/products/' + handle + '.js').then(function (r) {
      return r.ok ? r.json() : null;
    });
    return cache[handle];
  }

  var productCache = {};

  // Gap-collection products, fetched once and cached by Promise (not just by
  // resolved value, so a render() call that lands while the prefetch is still
  // in flight awaits the same single request instead of starting a second
  // one). Collection membership doesn't change within a session, so there's
  // no cache-invalidation need here.
  var gapCollectionCache = {};
  function fetchGapCollection(handle) {
    if (!gapCollectionCache[handle]) {
      gapCollectionCache[handle] = fetch('/collections/' + handle + '/products.json?limit=10')
        .then(function (r) { return r.ok ? r.json() : { products: [] }; })
        .then(function (data) { return data.products || []; });
    }
    return gapCollectionCache[handle];
  }

  function computeUpgradeCandidate(cart, excludeHandle) {
    var thresholdCents = window.PB_FREE_SHIPPING_THRESHOLD_CENTS || 20000;
    if (cart.total_price >= thresholdCents) return Promise.resolve(null);

    // sort lines by current line value, highest first -- "highest-value eligible line only"
    var lines = cart.items.slice().sort(function (a, b) {
      return b.original_line_price - a.original_line_price;
    });

    var checkNext = function (idx) {
      if (idx >= lines.length) return Promise.resolve(null);
      var line = lines[idx];
      // Box sizes task (2026-10-05): the macaron product now has its own
      // size tiers (6/12/24) with their own per-piece prices, which would
      // otherwise make this generic cross-product upgrade logic start
      // matching it too -- but the macaron box already has a dedicated
      // same-product offer (DrawerMacaronOffer below) and must never show
      // the generic upgrade block (explicit task rule). excludeHandle is
      // passed in from the cart-drawer markup's own data attribute (the
      // macaron product's real handle), not hardcoded here, so this stays
      // a no-op for every other product.
      if (excludeHandle && line.handle === excludeHandle) return checkNext(idx + 1);
      var currentPieces = parsePieceCount(line.variant_title);
      if (!currentPieces) return checkNext(idx + 1);
      var currentPerPiece = (line.price / 100) / currentPieces;

      return fetchProductJSON(line.handle, productCache).then(function (product) {
        if (!product) return checkNext(idx + 1);
        var candidates = [];
        product.variants.forEach(function (v) {
          if (v.id === line.variant_id || !v.available) return;
          var pcs = parsePieceCount(v.title);
          if (!pcs) return;
          var perPiece = v.price / 100 / pcs;
          if (perPiece < currentPerPiece - 0.005) {
            var cartAfterSwap = cart.total_price - line.price + (v.price * line.quantity);
            candidates.push({
              variant: v,
              pieces: pcs,
              perPiece: perPiece,
              crossesThreshold: cartAfterSwap >= thresholdCents,
            });
          }
        });
        if (candidates.length === 0) return checkNext(idx + 1);

        // Primary = the next larger tier with a lower per-piece price (immediate
        // next tier up, not necessarily the cheapest overall). The >=200zl tier
        // is only ever a SECONDARY "free shipping" option, shown separately --
        // never merged into the primary pick, per Gokhan's decision.
        candidates.sort(function (a, b) { return a.pieces - b.pieces; });
        var nonCrossing = candidates.filter(function (c) { return !c.crossesThreshold; });
        var primary = nonCrossing.length ? nonCrossing[0] : candidates[0];

        var crossingCandidates = candidates
          .filter(function (c) { return c.crossesThreshold && c.variant.id !== primary.variant.id; })
          .sort(function (a, b) { return a.pieces - b.pieces; });
        var secondary = crossingCandidates.length ? crossingCandidates[0] : null;

        return {
          line: line,
          product: product,
          currentPerPiece: currentPerPiece,
          primary: primary,
          secondary: secondary,
        };
      });
    };

    return checkNext(0);
  }

  function money(amountCents) {
    return (amountCents / 100).toFixed(2).replace('.', ',') + ' zł';
  }

  // Tolerant template fill: locale strings render placeholders as "{{ key }}"
  // (with spaces, e.g. Shopify's own `| t` output), so a literal
  // .replace('{{key}}', ...) with no spaces never matches and leaves the raw
  // "{{ key }}" visible to the customer. Matches with or without inner
  // whitespace, globally, and leaves an unrecognized placeholder untouched
  // rather than silently producing "undefined".
  function fillTemplate(template, values) {
    return (template || '').replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
    });
  }

  // Writing innerHTML is itself a DOM mutation the observer below reacts to --
  // only write when content actually changed, or a self-triggered render loop
  // never settles. Compare the source HTML we last rendered rather than only
  // el.innerHTML: browsers serialize URL "&" as "&amp;", so the macaron
  // builder link otherwise looks different forever and is rewritten on every
  // pass even though its rendered DOM is unchanged.
  function setContent(el, html) {
    if (el.__pbRenderedHtml === html) return false;
    el.__pbRenderedHtml = html;
    el.innerHTML = html;
    return true;
  }

  var DrawerUpgrade = function () {
    function formatCopy(template, candidate, currentPerPiece) {
      return fillTemplate(template, {
        variant: candidate.variant.title,
        price: money(candidate.variant.price),
        pp: candidate.perPiece.toFixed(2).replace('.', ','),
        pp_current: currentPerPiece.toFixed(2).replace('.', ','),
      });
    }

    function buildOfferHtml(candidate, copy, isSecondary, lineKey, quantity) {
      var giftLine = candidate.crossesThreshold
        ? '<p class="drawer-upgrade__gift">' + (window.PB_STRINGS.giftLine || '') + '</p>'
        : '';
      var buttonClass = 'drawer-upgrade__button text-button' + (isSecondary ? ' drawer-upgrade__button--secondary' : '');
      return (
        '<p class="drawer-upgrade__copy">' + copy + '</p>' +
        giftLine +
        '<button type="button" class="' + buttonClass + '" data-line-key="' + lineKey + '" data-variant-id="' + candidate.variant.id + '" data-quantity="' + quantity + '">' +
        (window.PB_STRINGS.swapButton || 'Zamień') +
        '</button>'
      );
    }

    // ADD the new variant first; only zero out the original line once the add
    // has actually succeeded. Never remove-then-add -- a failed add must never
    // leave the cart with neither variant in it.
    function swapLine(lineKey, variantId, quantity, onError) {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: quantity,
            properties: { '_pb_src': 'drawer_upgrade' },
          }],
        }),
      }).then(function (addResponse) {
        if (!addResponse.ok) {
          onError();
          return null;
        }
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity: 0 }),
        }).then(function () {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        });
      }).catch(onError);
    }

    // cart is passed in (fetched once per pass by init()) rather than fetched
    // here -- see the shared-fetch note at the top of this file.
    function render(el, cart) {
      if (cart.items.length === 0) { el.hidden = true; setContent(el, ''); return Promise.resolve(false); }
      return computeUpgradeCandidate(cart, el.dataset.excludeHandle).then(function (result) {
        if (!result) { el.hidden = true; setContent(el, ''); return false; }

        var primaryCopy = formatCopy(window.PB_STRINGS.upgradeCopy, result.primary, result.currentPerPiece);
        var primaryHtml =
          '<div class="drawer-upgrade__offer drawer-upgrade__offer--primary">' +
          buildOfferHtml(result.primary, primaryCopy, false, result.line.key, result.line.quantity) +
          '</div>';

        var secondaryHtml = '';
        if (result.secondary) {
          var secondaryCopy = formatCopy(window.PB_STRINGS.upgradeCopyFreeShip, result.secondary, result.currentPerPiece);
          secondaryHtml =
            '<div class="drawer-upgrade__offer drawer-upgrade__offer--secondary">' +
            buildOfferHtml(result.secondary, secondaryCopy, true, result.line.key, result.line.quantity) +
            '</div>';
        }

        var html =
          '<div class="drawer-upgrade">' +
          primaryHtml +
          secondaryHtml +
          '<p class="drawer-upgrade__error" hidden></p>' +
          '</div>';
        el.hidden = false;
        if (!setContent(el, html)) return true; // unchanged -- existing listeners still valid

        var errorEl = el.querySelector('.drawer-upgrade__error');
        el.querySelectorAll('.drawer-upgrade__button').forEach(function (btn) {
          btn.addEventListener('click', function (e) {
            var target = e.currentTarget;
            target.disabled = true;
            errorEl.hidden = true;
            swapLine(
              target.dataset.lineKey,
              parseInt(target.dataset.variantId, 10),
              parseInt(target.dataset.quantity, 10),
              function () {
                target.disabled = false;
                errorEl.textContent = window.PB_STRINGS.swapError || '';
                errorEl.hidden = false;
              }
            );
          });
        });
        return true;
      });
    }
    return { render: render };
  }();

  // Macaron AOV flow: counts macaron boxes in the cart and renders the
  // "add same / build new" block while exactly one is present. Resolves to
  // { active, pinHandle } so init() can tell DrawerGapSuggestions whether to
  // suppress itself (active === true) or pin a product handle first
  // (macaronQty >= 2), without DrawerGapSuggestions needing to know anything
  // about macarons itself.
  var DrawerMacaronOffer = function () {
    function macaronLines(cart, productId) {
      return cart.items.filter(function (item) { return item.product_id === productId; });
    }

    // line.url already carries "?variant=...", so appending a second "?pb_src="
    // would produce a malformed two-"?" URL -- always append with the right
    // separator for whatever query string (if any) is already there.
    function addQueryParam(url, key, value) {
      var separator = url.indexOf('?') === -1 ? '?' : '&';
      return url + separator + key + '=' + encodeURIComponent(value);
    }

    // cart is passed in -- see the shared-fetch note at the top of this
    // file. Logic is fully synchronous (no fetch of its own left except the
    // add-to-cart POST on click), but still returns a Promise so init()'s
    // .then() call site doesn't need to change.
    function render(el, cart) {
      var productId = parseInt(el.dataset.productId, 10);
      var flavorsKey = el.dataset.flavorsKey;
      var pinHandle = el.dataset.pinHandle;
      // Box sizes task (2026-10-05): a single box at or above this piece
      // count is already close enough to free shipping (e.g. the 24-piece
      // box, 169 zl / 31 zl short) that a cheap gap-closer item serves the
      // customer better than an "add a whole second box" nudge -- so this
      // block hides for that size and lets DrawerGapSuggestions run
      // unsuppressed instead. Data-driven from the cart-drawer markup, not
      // a hardcoded "24" in this file.
      var plusOneMaxPieces = parseInt(el.dataset.plusOneMaxPieces, 10) || Infinity;

      var lines = macaronLines(cart, productId);
      var qty = lines.reduce(function (sum, l) { return sum + l.quantity; }, 0);

      if (qty !== 1) {
        el.hidden = true;
        setContent(el, '');
        return Promise.resolve({ active: false, pinHandle: qty >= 2 ? pinHandle : null });
      }

      var line = lines[0];
      var pieces = parsePieceCount(line.variant_title);
      if (pieces && pieces > plusOneMaxPieces) {
        el.hidden = true;
        setContent(el, '');
        return Promise.resolve({ active: false, pinHandle: null });
      }

      var flavorsValue = (line.properties && line.properties[flavorsKey]) || '';
      var builderUrl = addQueryParam(line.url || el.dataset.builderUrl, 'pb_src', 'macaron_new_drawer') + '#box-builder-start';

      // "Compose new" first/primary, "same box" second/secondary (2026-10-05
      // revision): A2 found 3/3 two-box orders so far picked different
      // flavours each time, so the data points toward "new" being the more
      // likely choice, not an even toss-up.
      var html =
        '<div class="drawer-macaron">' +
        '<a class="drawer-macaron__button drawer-macaron__button--new drawer-macaron__button--primary" href="' + builderUrl + '">' +
        (el.dataset.newButton || '') +
        '</a>' +
        '<button type="button" class="drawer-macaron__button drawer-macaron__button--same drawer-macaron__button--secondary text-button" data-variant-id="' + line.variant_id + '">' +
        fillTemplate(el.dataset.sameButton, { price: money(line.price) }) +
        '</button>' +
        '<p class="drawer-macaron__error" hidden></p>' +
        '</div>';
      el.hidden = false;
      if (!setContent(el, html)) return Promise.resolve({ active: true, pinHandle: null });

      var errorEl = el.querySelector('.drawer-macaron__error');
      var sameBtn = el.querySelector('.drawer-macaron__button--same');
      sameBtn.addEventListener('click', function () {
        sameBtn.disabled = true;
        errorEl.hidden = true;
        var props = {};
        if (flavorsValue) props[flavorsKey] = flavorsValue;
        props['_pb_src'] = 'macaron_same_drawer';
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: line.variant_id, quantity: 1, properties: props }] }),
        }).then(function (res) {
          if (!res.ok) throw new Error('macaron add failed');
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }).catch(function () {
          sameBtn.disabled = false;
          errorEl.textContent = el.dataset.error || '';
          errorEl.hidden = false;
        });
      });

      return Promise.resolve({ active: true, pinHandle: null });
    }
    return { render: render };
  }();

  var DrawerGapSuggestions = function () {
    // cart is passed in; the gap collection comes from fetchGapCollection's
    // cache (prefetched at page load, see the shared-fetch note at the top
    // of this file) instead of a fresh fetch -- no network request on this
    // add path at all once that prefetch has resolved once.
    // Returns a Promise<boolean> (did this end up showing content).
    function render(el, cart, options) {
      options = options || {};
      if (options.suppressed) { el.hidden = true; setContent(el, ''); return Promise.resolve(false); }
      if (cart.items.length === 0) { el.hidden = true; setContent(el, ''); return Promise.resolve(false); }

      var thresholdCents = window.PB_FREE_SHIPPING_THRESHOLD_CENTS || 20000;
      var gap = thresholdCents - cart.total_price;
      var underThreshold = gap > 0;

      // Exclude anything already in the cart -- by product id, not variant id,
      // since a customer who already added one variant of a product doesn't
      // need it suggested again as a "complete your order" gap item. Filtered
      // locally against the cart already fetched for this pass, not a fresh
      // request.
      var cartProductIds = cart.items.map(function (item) { return item.product_id; });

      var collectionHandle = el.dataset.gapCollection;
      return fetchGapCollection(collectionHandle).then(function (allProducts) {
          var products = allProducts.filter(function (p) {
            return p.variants && p.variants[0] && p.variants[0].available && cartProductIds.indexOf(p.id) === -1;
          });
          if (products.length === 0) { el.hidden = true; setContent(el, ''); return false; }

          if (underThreshold) {
            products.sort(function (a, b) {
              var pa = parseFloat(a.variants[0].price), pb = parseFloat(b.variants[0].price);
              var aCloses = pa * 100 >= gap ? 0 : 1;
              var bCloses = pb * 100 >= gap ? 0 : 1;
              if (aCloses !== bCloses) return aCloses - bCloses;
              return pa - pb;
            });
            // Macaron 2+ state only (see options.pinHandle): pin a specific
            // product handle first regardless of the price-based sort above --
            // e.g. steer toward Zestaw Premium rather than the cheapest closer.
            // No-op for every other cart (pinHandle is only ever set that way).
            if (options.pinHandle) {
              var pinIdx = -1;
              for (var i = 0; i < products.length; i++) {
                if (products[i].handle === options.pinHandle) { pinIdx = i; break; }
              }
              if (pinIdx > 0) {
                var pinned = products.splice(pinIdx, 1)[0];
                products.unshift(pinned);
              }
            }
          }
          var shown = products.slice(0, 3);

          var header = underThreshold
            ? '<p class="drawer-gap__header">' + fillTemplate(window.PB_STRINGS.gapHeader, { gap: (gap / 100).toFixed(2).replace('.', ',') }) + '</p>'
            : '<p class="drawer-gap__header">' + (window.PB_STRINGS.complementaryHeader || '') + '</p>';

          var itemsHtml = shown.map(function (p) {
            var v = p.variants[0];
            return '<div class="drawer-gap__item">' +
              '<img src="' + (p.images[0] ? p.images[0].src.replace(/\.(jpg|png|webp)/, '_200x$&') : '') + '" alt="' + p.title + '" loading="lazy" width="72" height="72">' +
              '<div class="drawer-gap__item-info"><span class="drawer-gap__item-title">' + p.title + '</span><span class="drawer-gap__item-price">' + money(parseFloat(v.price) * 100) + '</span></div>' +
              '<button type="button" class="drawer-gap__add text-button" data-variant-id="' + v.id + '">' + (window.PB_STRINGS.addButton || '+ Dodaj') + '</button>' +
              '</div>';
          }).join('');

          var html = '<div class="drawer-gap">' + header + '<div class="drawer-gap__row">' + itemsHtml + '</div></div>';
          el.hidden = false;
          if (!setContent(el, html)) return true; // unchanged -- existing listeners still valid

          el.querySelectorAll('.drawer-gap__add').forEach(function (btn) {
            btn.addEventListener('click', function (e) {
              var b = e.currentTarget;
              b.disabled = true;
              fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  items: [{
                    id: parseInt(b.dataset.variantId, 10),
                    quantity: 1,
                    properties: { '_pb_src': 'drawer_gap' },
                  }],
                }),
              }).then(function () {
                document.dispatchEvent(new CustomEvent('cart:refresh'));
              });
            });
          });
          return true;
        });
    }
    return { render: render };
  }();

  function init() {
    fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
      // Fetched once here and passed to all three -- see the shared-fetch
      // note at the top of this file (was three separate, redundant
      // /cart.js fetches, one per widget).
      var upgradeEl = document.querySelector('[data-drawer-upgrade]');
      if (upgradeEl) DrawerUpgrade.render(upgradeEl, cart);

      var gapEl = document.querySelector('[data-drawer-gap]');
      var macaronEl = document.querySelector('[data-drawer-macaron]');
      if (macaronEl) {
        DrawerMacaronOffer.render(macaronEl, cart).then(function (result) {
          result = result || { active: false, pinHandle: null };
          if (gapEl) {
            DrawerGapSuggestions.render(gapEl, cart, { suppressed: result.active, pinHandle: result.pinHandle });
          }
        });
      } else if (gapEl) {
        DrawerGapSuggestions.render(gapEl, cart, {});
      }
    });

    // The drawer's Google-rating badge (assets/google-rating-badge.js) gets
    // wiped and recreated on every theme-driven redraw same as everything
    // else in here -- reuse this file's own MutationObserver instead of that
    // file adding a second, competing one.
    if (window.PBGoogleRatingBadge) window.PBGoogleRatingBadge.renderAll();
  }

  // The theme's own CartDrawer class (assets/app.js) replaces the entire drawer's
  // innerHTML wholesale on every quantity change, remove, refresh, AND on our own
  // dispatched 'cart:refresh' after a swap/add -- there is no single reliable
  // "done" event for all of those paths. A MutationObserver on the persistent
  // outer <cart-drawer> element catches every case where the drawer's own DOM
  // gets touched. Belt-and-suspenders: also listen directly for 'cart:refresh'
  // and 'cart:item-added' (both dispatched on `document` app-wide, e.g. by
  // assets/product.js on every successful add-to-cart, regardless of which
  // page/button triggered it) in case some future add-to-cart path ever
  // updates cart state without touching the drawer's DOM while it's closed.
  var debounceTimer = null;
  function debouncedInit() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(init, 120);
  }

  // These components are rendered by init() itself. Ignore their own DOM
  // writes; only an external/cart-drawer replacement should schedule another
  // pass. This is defense-in-depth alongside the idempotent renderers above.
  var selfRenderedSelector = [
    '[data-drawer-upgrade]',
    '[data-drawer-macaron]',
    '[data-drawer-gap]',
    '[data-google-rating-badge]'
  ].join(',');

  function hasExternalMutation(mutations) {
    return mutations.some(function (mutation) {
      var target = mutation.target;
      return !target.closest || !target.closest(selfRenderedSelector);
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    // Prefetch the gap collection now, long before any add is likely to
    // happen, so DrawerGapSuggestions's own cache is already warm by the
    // time a real add triggers init() -- this is what makes "no network
    // request on the add path" true in the common case, not just a cache
    // that happens to help on a second render.
    var prefetchGapEl = document.querySelector('[data-drawer-gap]');
    if (prefetchGapEl && prefetchGapEl.dataset.gapCollection) {
      fetchGapCollection(prefetchGapEl.dataset.gapCollection);
    }

    init();
    var drawerRoot = document.getElementById('Cart-Drawer');
    if (drawerRoot) {
      var observer = new MutationObserver(function (mutations) {
        if (hasExternalMutation(mutations)) debouncedInit();
      });
      observer.observe(drawerRoot, { childList: true, subtree: true });
    }
    document.addEventListener('cart:refresh', debouncedInit);
    document.addEventListener('cart:item-added', debouncedInit);
  });
})();
